import './App.css';
import { createTheme, ScopedCssBaseline, ThemeProvider } from "@mui/material";
import CustomRoutes from "./CustomRoutes";
import store, { persistor } from './redux/store';
import { useEffect } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MinorUserProvider from './context/UserNameProvider';
import ReactGA from 'react-ga4';
import { assignABVariant } from './lib/abTest';
import useGA4Event from "./hooks/useGA4Event";

const highlight = "#2200BB";
const formDark = "#474747";
const bgHighlight = "#2200BB1A";

const theme = createTheme({
  typography: {
    fontFamily: ['Inter', 'sans-serif'].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
        contained: {
          backgroundColor: highlight,
        },
        outlined: {
          borderColor: "lightgray",
          padding: "0.5rem 1rem",
          borderRadius: "0.4rem",
          "&:hover": {
            backgroundColor: "transparent",
            borderColor: highlight,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "0.75rem",
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: highlight,
            borderWidth: "1px",
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          "&.Mui-checked": {
            color: "inherit",
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: formDark,
          "&.Mui-focused": {
            color: formDark,
          },
        },
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: formDark,
          "&.Mui-checked": {
            color: highlight,
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#ffffff',
        },
      },
    },
  },
  palette: {
    primary: {
      main: '#fbf3f3',
    },
    secondary: {
      main: '#484848',
    },
    piction: {
      background: '#ffffff',
      text: '#6c6c72',
      white: '#fff',
      highlight: highlight,
      bgHighlight: bgHighlight,
    },
    grayTones: {
      main: '#636363',
      light: '#eeeeee',
      lighter: '#f5f5f5',
      ultralight: '#fafafa',
      piction: '#E8E8E8',
      border: '#CDD5D0'
    },
    background: {
      default: '#ffffff',
    },
  },
  spacing: 8,
});

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

function App() {
  const sendGA4Event = useGA4Event();

  const options = {
    mode: 'setup',
    currency: 'usd',
  };

  useEffect(() => {
    ReactGA.initialize(process.env.REACT_APP_GA_TRACKING_ID);
  }, []);

  useEffect(() => {
    let ab_variant = assignABVariant();
    sendGA4Event('variant_assigned', {
      test_name: 'appt_booking_in_ret_intake_feature',
      variant: ab_variant,
    });
  }, [sendGA4Event]);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ThemeProvider theme={theme}>
            <ToastContainer />
            <Elements stripe={stripePromise} options={options}>
              <ScopedCssBaseline>
                <MinorUserProvider>
                  <CustomRoutes />
                </MinorUserProvider>
              </ScopedCssBaseline>
            </Elements>
          </ThemeProvider>
        </LocalizationProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
